import { useEffect, useRef, useState } from 'react';
import type { StreamEvent } from '@hypevault/shared-types';

/**
 * Redemptions Queue — v2 port of v1's redemptions.ejs.
 * Shows pending card / channel-point redemptions. Builds the queue from
 * incoming `cardRedemption` events; entries dwell for `displaySeconds` then drop.
 *
 * Config (all optional):
 *   maxRows: number               (default 5)
 *   displaySeconds: seconds       (default 30 — how long an entry lingers)
 *   accentColor: string           (default '#a78bfa')
 *   title: string                 (default 'Card Queue')
 *   showActionType: boolean       (default true)
 */

interface QueueEntry {
  id: string;
  viewerName: string;
  cardName: string;
  action: string;
  expiresAt: number;
}

const ACTION_LABEL: Record<string, string> = {
  message: 'Message',
  tts: 'TTS',
  sound: 'Sound',
  video: 'Video',
  custom: 'Custom',
};

export function RedemptionsQueueWidget({
  fields,
  latestEvent,
}: {
  fields: Record<string, unknown>;
  latestEvent: StreamEvent | null;
}) {
  const maxRows = Number(fields.maxRows ?? 5);
  const displaySeconds = Number(fields.displaySeconds ?? 30);
  const accent = String(fields.accentColor ?? '#a78bfa');
  const title = String(fields.title ?? 'Card Queue');
  const showActionType = (fields.showActionType as boolean | undefined) ?? true;

  const [queue, setQueue] = useState<QueueEntry[]>([]);
  const lastId = useRef<string | null>(null);

  useEffect(() => {
    if (!latestEvent || latestEvent.id === lastId.current) return;
    if (latestEvent.type !== 'cardRedemption') return;
    lastId.current = latestEvent.id;
    const entry: QueueEntry = {
      id: latestEvent.id,
      viewerName: latestEvent.viewerName,
      cardName: latestEvent.cardName,
      action: latestEvent.action,
      expiresAt: Date.now() + displaySeconds * 1000,
    };
    setQueue((q) => [...q, entry].slice(-maxRows));
  }, [latestEvent, displaySeconds, maxRows]);

  // Cull expired
  useEffect(() => {
    const t = setInterval(() => {
      const now = Date.now();
      setQueue((q) => q.filter((e) => e.expiresAt > now));
    }, 1000);
    return () => clearInterval(t);
  }, []);

  return (
    <div
      style={{
        background: 'linear-gradient(135deg, rgba(10,8,24,0.92), rgba(18,14,38,0.92))',
        border: `1px solid ${accent}40`,
        borderRadius: 14,
        padding: '12px 16px',
        fontFamily: "'Poppins', system-ui, sans-serif",
        color: 'white',
        position: 'relative',
        overflow: 'hidden',
        minWidth: 280,
      }}
    >
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 22,
          right: 22,
          height: 1,
          background: `linear-gradient(90deg, transparent, ${accent}, transparent)`,
          opacity: 0.6,
        }}
      />
      <div
        style={{
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: 1.4,
          textTransform: 'uppercase',
          color: 'rgba(255,255,255,0.5)',
          marginBottom: 10,
          display: 'flex',
          alignItems: 'center',
          gap: 6,
        }}
      >
        <span style={{ color: accent }}>🃏</span>
        {title}
        {queue.length > 0 && (
          <span
            style={{
              marginLeft: 'auto',
              fontSize: 10,
              fontWeight: 700,
              color: accent,
              background: `${accent}1f`,
              borderRadius: 4,
              padding: '1px 6px',
            }}
          >
            {queue.length}
          </span>
        )}
      </div>
      {queue.length === 0 ? (
        <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.3)' }}>No pending redemptions</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {queue.map((e, i) => (
            <div
              key={e.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '6px 8px',
                borderRadius: 6,
                background: i === 0 ? `${accent}1a` : 'rgba(255,255,255,0.02)',
                border: i === 0 ? `1px solid ${accent}40` : '1px solid transparent',
                animation: 'rq-in 0.3s ease-out both',
              }}
            >
              <span
                style={{
                  fontSize: 11,
                  fontWeight: 800,
                  color: accent,
                  width: 16,
                  textAlign: 'center',
                  flexShrink: 0,
                }}
              >
                {i + 1}
              </span>
              <div style={{ minWidth: 0, flex: 1 }}>
                <div
                  style={{
                    fontSize: 13,
                    fontWeight: 700,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {e.cardName}
                </div>
                <div
                  style={{
                    fontSize: 10,
                    color: 'rgba(255,255,255,0.55)',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  by {e.viewerName}
                </div>
              </div>
              {showActionType && (
                <span
                  style={{
                    fontSize: 9,
                    fontWeight: 700,
                    letterSpacing: 0.5,
                    color: accent,
                    background: `${accent}26`,
                    borderRadius: 4,
                    padding: '1px 5px',
                    textTransform: 'uppercase',
                    flexShrink: 0,
                  }}
                >
                  {ACTION_LABEL[e.action] ?? e.action}
                </span>
              )}
            </div>
          ))}
        </div>
      )}
      <style>{`
        @keyframes rq-in {
          from { opacity: 0; transform: translateX(-8px); }
          to { opacity: 1; transform: translateX(0); }
        }
      `}</style>
    </div>
  );
}
